import { isValidAnalysisRegion, type AnalysisRegion, type PhotoPurpose } from '@safetyhub/shared';
import api from './api';

/**
 * 머신러닝 소스 관리 — admin 조회 전용 API 클라이언트.
 *
 * 응답 계약 정본은 P4-d 계약 §2이고 스토리 계약은
 * `docs/decisions/2026-10-07-photo-lifecycle-scenario-spec.md`의 P5·P11이다.
 *
 * ⚠️ **조회 전용이다.** P28(admin에서 사진을 수동 삭제)은 계약이 「미래 기능」으로 명시적으로
 * 범위 밖에 둔 것이므로 여기에 삭제·수정 호출을 추가하지 말 것.
 */

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

/**
 * 사진 품질 등급 — 서버가 **읽기 쿼리로 파생**한다(칼럼 신설 없음, 정책 ⓖ).
 * 우선순위는 첫 일치로 `D1 → D2 → A → B → C`.
 *  - `D1` 사진이 삭제됨(사진 단위) / `D2` 점검이 삭제됨(점검 단위)
 *  - `A` 결재 완료 보고서에 실림 / `B` 보고서에 실렸으나 결재 완료 없음 / `C` 보고서에 안 실림
 */
export type MlPhotoGrade = 'A' | 'B' | 'C' | 'D1' | 'D2';

/**
 * 사진 용도 — ML 소스 화면의 **탭 축**이다(2026-10-07 사용자 결정: 소스를 메인 메뉴로 늘리지 않고
 * 「머신러닝 소스 관리」 한 메뉴 안의 탭으로 가른다).
 *
 * ⚠️ 값을 추가하면 `PHOTO_PURPOSE_LABELS`가 `Record<PhotoPurpose, string>`이라 **tsc가 라벨을 요구**한다.
 * 그 대신 화면은 **서버가 보낸 `purposeCounts`의 키**에서 탭을 파생하므로, 서버가 먼저 늘어나
 * 이 union에 없는 키가 와도 탭이 사라지지 않는다(라벨만 raw 키로 떨어진다).
 *
 * 2026-10-19: 손선언을 **shared 미러**로 바꿨다 — 값 집합의 정본은 Prisma `enum PhotoPurpose`이고
 * shared 선언이 그것과 같은지는 server 가드 spec이 잰다(`@safetyhub/shared`의 `PHOTO_PURPOSES`
 * 주석이 정본). admin은 `@prisma/client`를 import할 수 없으므로 미러를 본다.
 * 아래 재export는 `./api`·`pages/Inspections.tsx`가 이 모듈에서 이 이름을 가져가므로 유지한다.
 */
export type { PhotoPurpose };

/** 점검 상세·ML 목록이 공유하는 사진 형상(계약 §2-1). */
export interface AdminMlPhoto {
  id: string;
  url: string;
  thumbnailUrl: string | null;
  analysisStatus: string;
  createdAt: string;
  deletedAt: string | null;
  grade: MlPhotoGrade;
  purpose: PhotoPurpose;
}

/**
 * 사진 단위 **중점 분석 영역** — 0~1 «정규화 비율»이다(픽셀도 1000분율도 아니다).
 *
 * ⚠️ 1000으로 나누지 말 것. 메모리 `feedback-gemini-bbox-1000-scale`은 **AI 응답을 파싱하는
 * 단계**의 축이고, DB `inspection_photos.analysis_region`에 저장된 값은 이미 정규화돼 있다
 * (dev 8건 전수 실측 2026-10-07: 전부 0~1 · `x+w ≤ 1` · `y+h ≤ 1`, 최대 `x+w`=0.99).
 *
 * 형상 판정자는 shared `isValidAnalysisRegion` **하나**다 — 화면이 자기 검사를 또 쓰지 않는다
 * (그 함수의 JSDoc이 그 규약의 정본이고 서버 입구 셋·분석 폴백·web 그리기 UI가 같은 것을 쓴다).
 *
 * ⚠️ **타입 정본도 shared다 — 여기서 `{x,y,w,h}`를 다시 선언하지 말 것**(2026-10-08 정정).
 * 2026-10-08까지 이 줄이 구조 동일 **사본**이었다. 판정자는 shared에서 가져오면서 **어휘만
 * 자기가 적은 것**이라, 두 금지가 이름을 대고 막아 둔 바로 그 모양이었다:
 *   · `packages/shared/src/types/ai.ts`의 `AnalysisRegion` JSDoc — 「어휘를 web에서 또 선언하면
 *     갈라지는 날 그 **조용한 실패**가 돌아온다」
 *   · `packages/shared/src/utils/index.ts:82` — 「어휘를 web에서 또 선언하면
 *     `BoundingBox {width,height}`와 갈려 **「영역을 지정했는데 전체로 분석됐다」가 조용히 난다**」
 * 그 실패가 조용한 이유는 그쪽 JSDoc에 있다 — 형상이 어긋난 값은 입구에서 400으로 거부되지만,
 * 거부를 못 받는 자리(낡은 jsonb·psql 직접 입력)에서는 분석이 `logger.warn` 한 줄만 남기고
 * **전체 분석으로 떨어진다.** 즉 사용자에게는 아무 표시가 없다.
 *
 * 별칭을 **남겨 두는** 이유는 이 파일의 `AdminMlPhoto` 계약이 그 이름으로 그 칸을 부르기
 * 때문이다(이름을 지우면 계약 서술이 shared 어휘로 흩어진다) — 지우지 말고 **별칭으로** 둘 것.
 */
export type AdminMlAnalysisRegion = AnalysisRegion;

/** ML 목록 행 — 전조직이므로 소속(조직·현장)과 점검 식별자가 더 붙는다(계약 §2-3). */
export interface AdminMlPhotoRow extends AdminMlPhoto {
  inspectionId: string;
  inspectionDeletedAt: string | null;
  companyId: string;
  /** 파기된 조직은 서버가 '삭제된 조직'으로 스크러빙해 내린다 — 그대로 노출한다. */
  companyName: string;
  /** 같음 — '삭제된 현장'. */
  siteName: string;

  // ── 라벨 품질(P4-f 계약 §3) ─────────────────────────────────────────────
  // `grade`가 **문서화·삭제 상태**를 말하는 것과 별개로, 여기는 **분석 내용이 얼마나 채워졌나**를
  // 말한다. ⚠️ 둘 다 **성질**이고 어느 쪽도 「쓸 수 있나/없나」가 아니다 — 모집단을 고르는 것은
  // 학습 단계의 일이다(`ML_PHOTO_GRADE_COLORS` JSDoc에 근거·결정 출처).
  // 등급만으로는 「A등급 98장 중 어느 것이 학습에 값있나」에 답할 수 없었다.
  //
  // ⚠️ **없을 때 `null`이고 빈 문자열이 아니다.** 빈 문자열로 뭉개면 화면에서
  // 「분석이 없다」와 「서버가 칸을 빠뜨렸다」가 같은 모양이 된다.
  /** AI 분석 한 줄 요약. dev 273장 중 266장(97%)이 채워져 있다. */
  analysisSummary: string | null;
  /** 위험 요약. dev 142장(52%) — 요약보다 훨씬 비어 있으므로 빈 칸을 숨기지 말 것. */
  riskSummary: string | null;
  improvementPlan: string | null;
  /** 비면 `[]`(문자열 배열 — 실측 형상 `["추락","화재","누출"]`). */
  riskTypes: string[];
  /** 같음 — 비면 `[]`. */
  focusCategories: string[];
  processType: string | null;
  /** 사람이 검수했나. dev 91장(33%)인데 **admin 화면에는 지금까지 0건** 노출됐다. */
  isReviewed: boolean;
  analysisRegion: AdminMlAnalysisRegion | null;

  /**
   * 발견사항 **전수**(삭제분 포함) — ⚠️ **`inspection_findings`를 센다**(`ai_analysis_logs`가 아니다).
   * 후자는 사진의 **71%만** 덮으므로 29%(dev 78장)에 **거짓 0**을 그린다.
   */
  findingCount: number;
  /**
   * `findingCount` 중 삭제된 것(dev 전체 23건) — ⚠️ **아래 두 칸의 분모를 깎는 값이다.**
   *
   * dev에 발견사항이 **전부 삭제된** 사진이 1장 있다(`total=2 active=0`). 이 칸이 없으면 그 사진이
   * 화면에서 「발견사항 2건」으로만 보인다.
   */
  findingDeletedCount: number;
  /**
   * `aiEdited = true` — **살아 있는 것 중에서만** 센다(서버 확정).
   *
   * ⚠️ **`aiEdited`는 「AI 설명을 사람이 교정했다」가 아니다 — 「사람이 손댔다」(넓은 뜻)다.**
   * 이름이 실제보다 좁은 뜻을 암시하는 칼럼이라 화면 문구가 그 함정을 그대로 물려받는다.
   *
   * `true`가 되는 자리가 **다섯**이고(`inspections/inspection-finding.service.ts` :431 ·:1252
   * ·:1361 ·:1484 ·:1579) 그중 **넷이 대책·조치(`aiAnalysisDetail`) 수정**이다. 더구나 그 넷은
   * `aiGenerated`를 **묻지 않고** 찍으므로 **AI가 만들지도 않은 발견사항도 `true`가 된다**
   * (dev 실측 2026-10-07: `aiEdited` 96건 중 **9건이 `ai_generated=false`**).
   *
   * ML 학습에 쓰이는 **「교정 쌍」**(AI 입력 → 사람 정답)은 `original_description ≠ description`
   * 기준 **2건**뿐이고 **다른 축**이다. 그래서 화면에 「교정」·「정정」이라 쓰면 **96건 중 94건에
   * 대해 거짓**이 된다 — 라벨은 `사람 수정`이다(아래 `deriveFindingLabel`).
   *
   * ⚠️ 이 뜻은 **`schema.prisma`에도 서비스 JSDoc에도 적혀 있지 않다**(주석 0건 — 그래서 팀장이
   * 한 번 오독했다). 이 JSDoc이 그 축의 유일한 기록일 수 있으니 지우지 말 것.
   */
  findingEditedCount: number;
  /**
   * 「내보내기 처리」를 거친 발견사항 — **살아 있는 것 중에서만** 센다(서버 확정).
   *
   * ⚠️ **이 칼럼의 뜻이 2026-10-12에 좁아져 행 집단이 둘이다**(`knowledgeExported`:
   * 「한 번 판정을 거쳤다」 → 「**등록에 성공했다**」). 그래서 이 칸만으로는 「학습에 들어갔다」를
   * 단정할 수 없다 — 두 집단의 서술은 `KNOWLEDGE_EXPORT_TOOLTIP` JSDoc이 정본이고, 서버 쪽
   * 정본은 `admin-ml.service.ts`의 같은 칸 JSDoc이다.
   *
   * 분수의 **분모는 아래 `findingExportTargetCount`**다 — 살아 있는 전수를 분모로 쓰면
   * **등록될 수 없는 발견사항이 하나라도 있는 사진이 영구히 「일부」**가 된다.
   */
  findingExportedCount: number;
  /**
   * **내보내기 대상** 발견사항 — 「등록 가능한 것 ∪ 이미 처리된 것」(서버가 센다).
   * 화면의 「전부/일부」 **분모**이고, `findingExportedCount ⊆ 이 수`가 **구조적으로** 참이다.
   *
   * ⚠️ **살아 있는 전수(`findingCount − findingDeletedCount`)로 되돌리지 말 것.** 플래그가
   * 「등록 성공」을 뜻하게 된 뒤로는 비AI·신뢰도 미달 발견사항이 분자에 **영원히** 못 들어가므로,
   * 그런 행이 하나라도 있는 사진은 **언제까지나 「일부」**가 된다 — 화면이 「일부」라 말하면
   * 운영자는 **남은 것을 찾으러 가고 찾을 것이 없다**(2026-10-07에 분모를 좁혀 닫은 피해와 같은
   * 모양이다). 술어·잔여·되짚을 조건은 서버 쪽 `findingExportTargetCount` JSDoc이 정본이다.
   */
  findingExportTargetCount: number;
  /**
   * ⓒ-3 링크 게이트 — ⚠️ 이쪽은 **`ai_analysis_logs`** 기준이 맞다(분석 이력 화면이 그 테이블을
   * 보여주므로). dev 273장 중 **78장(29%)이 0건**이라, 무조건 링크를 그리면 그 29%가 빈 화면으로 간다.
   *
   * 두 집계가 **다른 테이블**을 세므로 이름을 합치지 않는다 — 합치면 97%/71% 차이가 숨는다.
   */
  analysisLogCount: number;
}

export interface MlPhotosResponse {
  items: AdminMlPhotoRow[];
  total: number;
  page: number;
  limit: number;
  /**
   * ⚠️ **`grade`·`purpose` 필터와 무관하게** 센 분포 — 필터를 걸어도 안 바뀌어야 운영자가
   * 「왜 이 사진이 학습에 안 들어가나」에 답할 수 있다.
   *
   * ⚠️ 다만 **「전체」가 아니다** — `companyId`·`includeDeleted` **스코프는 따른다**(서버 확정,
   * 2026-10-07). 그래서 조직 필터를 걸면 이 분포는 *그 조직의* 분포다. 화면 문구가 이것을
   * 「전체」라고 부르면 거짓이 되므로 `MlSources`는 조직 필터 유무로 라벨을 바꾼다.
   *
   * 서버 불변식: `grade` 미지정일 때 `sum(gradeCounts) === total`.
   */
  gradeCounts: Record<MlPhotoGrade, number>;
  /**
   * 같은 스코프 규칙. 0건 purpose도 `0`으로 내려온다 — 키가 없으면 프론트가 그 탭을 못 그리고
   * 그러면 「없다」와 「빠뜨렸다」가 같은 모양이 된다.
   *
   * ⭐ `gradeCounts`와 **스코프가 같아서** 두 합을 대조할 수 있다(`MlSources`의 검산). 한쪽만
   * 스코프를 따르면 그 대조가 성립하지 않으니, 서버를 고칠 때 이 짝을 깨지 말 것.
   */
  purposeCounts: Record<PhotoPurpose, number>;
}

/** 개선 이미지(P11) — 이미지와 요약은 보이고 출처 보고서는 비어 있을 수 있다. */
export interface AdminImprovementImage {
  id: string;
  imageUrl: string;
  mimeType: string;
  summaryText: string;
  aiModel: string;
  usageCount: number;
  createdAt: string;
  sourceInspectionId: string;
  /** ⭐ null이면 출처 보고서가 삭제된 것 — P11 계약의 핵심이라 화면에서 명시한다. */
  sourceReportId: string | null;
  sourcePhotoId: string | null;
  companyId: string;
  companyName: string;
  /** embedding 벡터 자체는 내리지 않는다(용량) — 유무만. */
  hasEmbedding: boolean;
}

export interface ImprovementImagesResponse {
  items: AdminImprovementImage[];
  total: number;
  page: number;
  limit: number;
}

/**
 * 탭 라벨 — `Record<PhotoPurpose, string>`이라 union이 늘면 tsc가 라벨을 요구한다.
 * **키 순서가 탭 순서**다(서버 응답의 키 순서에 화면 배치를 맡기지 않는다).
 */
export const PHOTO_PURPOSE_LABELS: Record<PhotoPurpose, string> = {
  HAZARD_CHECK: '점검 사진',
  BEST_PRACTICE: '모범사례 사진',
  OVERVIEW_PHOTO: '전경 사진',
};

/**
 * 기본 탭 — URL `?tab=`이 없을 때. dev·prod 모두 압도적 다수인 용도를 기본으로 둔다.
 * (탭 목록 자체는 서버 카운트에서 파생하므로 이 값은 「처음 보이는 탭」만 정한다.)
 */
export const DEFAULT_PHOTO_PURPOSE: PhotoPurpose = 'HAZARD_CHECK';

/**
 * 개선 이미지 탭의 키 — purpose가 아니라 **별도 소스**다.
 * 소문자-하이픈이라 SCREAMING_SNAKE인 purpose enum과 **구조적으로 충돌할 수 없다**.
 */
export const IMPROVEMENT_IMAGES_TAB = 'improvement-images';

export const IMPROVEMENT_IMAGES_TAB_LABEL = '개선 이미지';

/** 등급 전수 — 요약 칩·필터 select가 이 순서로 그린다. */
export const ML_PHOTO_GRADES: readonly MlPhotoGrade[] = ['A', 'B', 'C', 'D1', 'D2'];

/**
 * 등급 라벨 — 코드값(A/B/C)만 두지 않고 뜻을 함께 적는다(P5 계약).
 *
 * ⚠️ **라벨은 「정의 그대로」다 — 정책 판정을 섞지 말 것.** A=결재 완료 보고서에 실림 ·
 * B=보고서에 실렸으나 미결재 · C=보고서에 안 실림 · D1=사진 삭제 · D2=점검 삭제.
 * 「사용 가능」·「미사용」·「제외」·「학습 제외」로 바꾸지 마라 — **등급은 제외 기준이 아니고**
 * 모집단 선택은 학습 단계의 일이다(아래 `ML_PHOTO_GRADE_COLORS` JSDoc에 근거와 결정 출처).
 */
export const ML_PHOTO_GRADE_LABELS: Record<MlPhotoGrade, string> = {
  A: '결재 완료',
  B: '보고서 작성중',
  C: '보고서 없음',
  D1: '사진 삭제',
  D2: '점검 삭제',
};

/**
 * 등급 색 — ⚠️ **색이 정하는 것은 「한눈에 갈리나」뿐이다. 「쓸 수 있나」가 아니다.**
 *
 * ## 종전 서술이 틀렸다 (2026-10-07 정정)
 *
 * 여기 「A=초록(**사용 가능**) · C=회색(**미사용**) · D=빨강(**제외**)」이라 적혀 있었다. 그것은
 * **정책 판정**이고 등급은 **성질 기록**이다 — ⓖ 결정이 반대로 못박고 있다
 * (`docs/decisions/2026-10-07-photo-lifecycle-scenario-spec.md` ⓖ 근거 2): **「학습 단계에서
 * 고른다 — 「A만」·「A+B」를 그때 정한다. 지금 정할 필요가 없다.」**
 *
 * 더구나 **D는 「제외」의 반례다**: 사용자 판단이 「**삭제 시에 보관을 하되 추적이 돼야해. 이는
 * ML 등에서 활용을 할 예정이니**」였다(`2026-10-06-deleted-photos-are-archived-de-identified.md`)
 * — **지운 사진도 ML에 쓴다.** 「제외」라 적어 두면 다음 사람이 그 어휘를 화면 문구로 옮기고,
 * 그러면 운영자가 **보관하기로 결정한 모집단을 스스로 버린다.**
 *
 * ## 못박아 둘 한 줄
 *
 * **등급은 제외 기준이 아니다 — 모집단 선택은 학습 단계의 일이다**(ⓖ 근거 2). 이 화면의 몫은
 * **무엇이 어떤 성질인지**를 보여주는 것이고 **고르는 것은 나중**이다(ⓖ 근거 3: 「인정된 것만」
 * 보여주면 「왜 이 사진이 학습에 안 들어가나」에 답할 화면이 없다).
 *
 * ## 색 선택 — 왜 D를 중립색으로 «옮기지 않았나»
 *
 * 요구는 둘이고 서로 당긴다: ① **A와 D가 한눈에 갈려야 한다** ② **D가 「쓰면 안 되는 것」으로
 * 읽히면 안 된다.** 빨강을 중립색(회색·슬레이트)으로 옮기면 ②는 풀리는데 **①이 깨지고**
 * (C가 이미 회색이다) 그보다 나쁜 것이 하나 더 생긴다 — **같은 사실에 색이 둘이 된다**:
 * 삭제는 이미 이 화면과 **점검 상세**에서 빨강이다(`MlPhotoThumbnail`의 「삭제됨」 오버레이 ·
 * `(점검 삭제)` 텍스트). 그 오버레이는 **「지운 사진의 시각 구별」의 단일 소유자**라 함께 옮길
 * 수 없고(점검 상세와 공유한다), 배지만 옮기면 한 사진에서 **빨강 오버레이 + 중립 배지**가 된다.
 *
 * ⇒ **빨강 계열은 유지하되 「채움」을 덜었다.** D는 이제 채운 블록이 아니라 **흰 바탕 + 빨간
 * 테두리·글자**다 — 경보의 무게(채운 빨강 = 금지)를 덜면서 ①을 지킨다. 라벨은 이미 **사실**
 * 이다(「사진 삭제」·「점검 삭제」 — 「제외」가 아니다). D1·D2는 테두리 굵기로 갈라 **라벨을 못
 * 읽어도** 구별된다(종전 성질 유지).
 *
 * ⚠️ 되짚을 조건: 운영자가 **실제로** D를 「쓸 수 없는 것」으로 읽는다는 신호가 나오면, 색이
 * 아니라 **등급 분포 칩의 머리말**(「등급 분포」)에 「전부 보관·학습 후보」를 적는 쪽이 낫다 —
 * 색은 네 등급을 갈라야 해서 뜻을 실을 자리가 좁다.
 */
export const ML_PHOTO_GRADE_COLORS: Record<MlPhotoGrade, string> = {
  A: 'bg-green-100 text-green-700',
  B: 'bg-yellow-100 text-yellow-700',
  C: 'bg-gray-100 text-gray-600',
  D1: 'bg-white text-red-700 ring-1 ring-red-300',
  D2: 'bg-white text-red-700 ring-2 ring-red-400',
};

/**
 * 출처 보고서가 없을 때의 문구 — **ML 목록과 점검 상세가 같은 문구를 써야** 하므로
 * 두 화면이 각자 리터럴을 두지 않고 여기서 가져간다(계약 §4-2-2).
 */
export const SOURCE_REPORT_DELETED_LABEL = '출처 보고서 삭제됨';

export interface MlPhotosParams {
  page?: number;
  limit?: number;
  grade?: MlPhotoGrade;
  purpose?: string;
  companyId?: string;
  /**
   * 기본 **true** — ML 화면은 전수가 기본이다(지운 사진도 보여주고 등급으로 가른다).
   * 명시적으로 `false`를 넘길 때만 제외되도록 `!== undefined`로 검사한다.
   */
  includeDeleted?: boolean;
}

export interface ImprovementImagesParams {
  page?: number;
  limit?: number;
  companyId?: string;
}

export const mlSourcesApi = {
  async listPhotos(params?: MlPhotosParams): Promise<MlPhotosResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.grade) query.set('grade', params.grade);
    if (params?.purpose) query.set('purpose', params.purpose);
    if (params?.companyId) query.set('companyId', params.companyId);
    // 기본값이 true이므로 false일 때만 보낸다 — `if (params?.includeDeleted)`로 쓰면
    // false를 「미지정」과 구분하지 못해 전수 제외 의도가 조용히 사라진다.
    if (params?.includeDeleted !== undefined) {
      query.set('includeDeleted', String(params.includeDeleted));
    }
    const qs = query.toString();
    const res = await api.get<ApiResponse<MlPhotosResponse>>(
      `/admin/ml/photos${qs ? `?${qs}` : ''}`,
    );
    return res.data.data;
  },

  async listImprovementImages(
    params?: ImprovementImagesParams,
  ): Promise<ImprovementImagesResponse> {
    const query = new URLSearchParams();
    if (params?.page) query.set('page', String(params.page));
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.companyId) query.set('companyId', params.companyId);
    const qs = query.toString();
    const res = await api.get<ApiResponse<ImprovementImagesResponse>>(
      `/admin/ml/improvement-images${qs ? `?${qs}` : ''}`,
    );
    return res.data.data;
  },
};

/**
 * 카운트 맵의 합 — 「사진 전체 N장」과 두 분포의 교차 검산에 쓴다.
 * 서버가 purpose·grade 키 하나를 빠뜨리면 두 합이 어긋나므로 **숫자로** 드러난다.
 */
export function sumCounts(counts: Record<string, number> | undefined): number {
  if (!counts) return 0;
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

/**
 * 등급 칩·필터 키 목록 — **알려진 등급 전수(0건 포함) + 서버가 보낸 미지 키를 뒤에.**
 *
 * ⚠️ **`ML_PHOTO_GRADES.map(...)`만 쓰면 안 된다** — 서버가 먼저 늘어나 새 등급 코드(`'E'`)를
 * 보내면 **그 건수가 분포에서 조용히 사라진다.** 그러면 「그 등급이 0건이다」와 「프론트가
 * 빠뜨렸다」가 같은 모양이 되는데, 그것이 이 파일이 `derivePurposeTabs`·`purposeTabLabel`로
 * 반복해 막아 온 실패다(그 JSDoc의 성질 3). 2026-10-08까지 `MlSources`의 등급 필터 select와
 * `GradeSummary`가 정확히 그 모양이었다.
 *
 * ⚠️ **머리말의 검산(`countsDisagree`)은 이 축을 못 잡는다.** 그쪽은 `sumCounts`로 **전 키를**
 * 합산해 purpose 합과 대조하므로 미지 등급 키도 **양쪽 합에 들어간다** — 즉 합은 일치하고
 * 칩만 사라진다. 탐지기는 이 함수의 짝 spec과 `MlSources`의 렌더 케이스다.
 *
 * ## ⚠️ `Inspections.tsx`의 `orderedGradeKeys`와 «반만» 같다 — 0건 축이 반대다
 *
 * 그 자리는 `ML_PHOTO_GRADES.filter((g) => gradeCounts[g])`로 **등장한 등급만** 그린다. 같은
 * 형태를 여기 베끼면 안 된다 — 이 화면은 **0건 등급도 보여야 한다**(그 파일 주석이 그 경계를
 * 적는다: 「전수 분포에서 0건을 보여야 하는 것은 ML 화면 쪽 요구다」). 즉 두 자리가 공유하는
 * 것은 **「미지 키를 버리지 않는다」 축 하나**이고 0건 축은 요구가 갈린다. 그래서 공용 함수로
 * 합치지 않고 이 쪽만 여기 둔다.
 *
 * 응답이 오기 전(`undefined`)에는 알려진 등급만 돌려줘 칩·select가 깜빡이지 않게 한다.
 */
export function deriveGradeKeys(gradeCounts: Record<string, number> | undefined): string[] {
  const known: string[] = [...ML_PHOTO_GRADES];
  if (!gradeCounts) return known;
  const unknown = Object.keys(gradeCounts)
    .filter((key) => !known.includes(key))
    .sort();
  return [...known, ...unknown];
}

/**
 * 등급 라벨 — 라벨 맵에 없는(= 서버가 먼저 늘어난) 코드는 **코드만** 보여 존재를 숨기지 않는다.
 *
 * `purposeTabLabel`과 같은 규율이고, `PhotoGradeBadge`가 배지 안에서 이미 그 폴백을 갖는다
 * (그 JSDoc: 「서버가 먼저 늘어나 `'E'` 같은 새 코드를 보내면 그 코드를 그대로 그린다」).
 * 이 함수는 **배지 밖의 텍스트**(필터 select의 `코드 · 뜻`)를 위한 짝이다 — 없으면 미지 코드의
 * option이 「E · undefined」로 그려진다.
 */
export function mlPhotoGradeLabel(grade: string): string {
  return ML_PHOTO_GRADE_LABELS[grade as MlPhotoGrade] ?? '';
}

/**
 * 탭 키 목록 — **서버가 보낸 `purposeCounts`의 키**에서 파생한다.
 *
 * 세 성질이 요구사항이다:
 *  1. 서버에 purpose가 추가되면 **프론트 수정 없이** 탭이 늘어난다(사용자 요구).
 *  2. 0건 purpose도 탭에서 빠지지 않는다 — 빠지면 「데이터가 없다」와 「서버가 빠뜨렸다」가
 *     같은 모양이 된다(`OVERVIEW_PHOTO`가 dev에서 0건이라 정확히 그 위험이 있다).
 *  3. 라벨 맵에 없는 키도 **버리지 않는다**(같은 이유). 순서는 라벨 맵 순서 → 그 뒤 미지 키.
 *
 * 응답이 오기 전에는 라벨 맵 키로 그려 탭 바가 깜빡이지 않게 한다.
 */
export function derivePurposeTabs(
  purposeCounts: Record<string, number> | undefined,
): string[] {
  const known = Object.keys(PHOTO_PURPOSE_LABELS);
  const serverKeys = purposeCounts ? Object.keys(purposeCounts) : [];
  if (serverKeys.length === 0) return known;
  const inKnownOrder = known.filter((k) => serverKeys.includes(k));
  const unknown = serverKeys.filter((k) => !known.includes(k)).sort();
  return [...inKnownOrder, ...unknown];
}

/**
 * 탭 라벨 — 라벨 맵에 없는(= 서버가 먼저 늘어난) 키는 **raw 키로 보여** 존재를 숨기지 않는다.
 *
 * ⚠️ 이 폴백을 「라벨 없는 키는 그리면 안 된다」로 읽고 지우지 말 것. 지우면 그 탭 이름이 빈
 * 문자열이 되어, **그 소스가 있다는 사실 자체**가 화면에서 사라진다 — 「없다」와 「빠뜨렸다」를
 * 가르려고 둔 장치다(`derivePurposeTabs`와 한 짝).
 */
export function purposeTabLabel(purpose: string): string {
  return PHOTO_PURPOSE_LABELS[purpose as PhotoPurpose] ?? purpose;
}

/* ─── 라벨 품질(P4-f ⓐ) 파생 ───────────────────────────────────────────────
 *
 * ⚠️ **이 파생을 「없으면 0」으로 단순화하지 말 것.** 이 축의 전체 요점이
 * 「**비었다는 사실 자체가 읽을 값**」이라는 것이고(⚠️ 「학습 제외 사유」라 적지 말 것 — 등급과
 * 같은 자다: 성질을 보여줄 뿐이고 **고르는 것은 학습 단계의 일**이다), 그래서 세 상태를
 * **구별해서** 말한다:
 *   ① 값이 있다 ② **없다**(= 분석·발견사항이 없다 — 그것이 정보다) ③ **모른다**(서버가 그 칸을
 *   안 내린다 — 아래 `*_UNKNOWN_LABEL` 참조).
 * ②를 빈 칸으로 두면 ③과 같은 모양이 되고, ③을 ②로 뭉개면 **화면이 거짓말을 한다.**
 *
 * 순수 함수로 둔 이유: 이 모듈을 `vi.mock`하는 유일한 spec이 `importOriginal`로 실제 모듈을 펼친 뒤
 * `mlSourcesApi`만 덮으므로(`pages/__tests__/mlSources.test.tsx` 헤더) 새 export가 그 mock을
 * 깨뜨리지 않고, 라벨 파생이 **검사 대상**으로 남는다. 팩토리 mock으로 바꾸면 그 성질이 사라진다.
 */

/** 분석 요약이 없을 때 — 빈 칸이 아니라 **문구**다(「없다」는 것 자체가 읽을 값이다). */
export const NO_ANALYSIS_SUMMARY_LABEL = '분석 없음';

/** 발견사항이 0건일 때. 같은 이유로 빈 칸으로 두지 않는다. */
export const NO_FINDINGS_LABEL = '발견사항 없음';

/**
 * 서버가 집계 칸을 **안 내릴 때**. 「0건」으로 그리면 **거짓**이므로 「모른다」를 따로 말한다.
 *
 * ⚠️ **이 분기를 「일어나지 않는 방어」로 읽고 지우지 말 것 — 구조적으로 도달한다.** server는
 * EC2(`deploy-ec2`)에, admin은 Cloudflare(`deploy-admin`)에 **별개 job으로** 배포되고 **게이트가
 * 다르다**: `deploy-admin`은 `prepare.outputs.admin`이 false면 skip인데 `deploy-ec2`에는 그 조건이
 * 없다. 즉 **한쪽만 먼저 배포되는 조합이 가능**하고, 그 창에서 이 화면은 그 칸이 없는 응답을 받는다
 * (팀장 확인 2026-10-07).
 */
export const FINDING_COUNT_UNKNOWN_LABEL = '집계 없음';

/** 사람이 검수한 사진 — dev 33%인데 admin에는 지금까지 노출이 0건이었다. */
export const PHOTO_REVIEWED_LABEL = '검수 완료';

/**
 * 「내보내기 처리」의 상태 — ⚠️ **세 상태를 구분한다**(`전부`/`일부`/`없음`).
 *
 * ⚠️ **「일부」는 dev 실데이터에 0건이다 — 그래도 뭉개지 않는다.** 사진 단위 실측(2026-10-07):
 * 올바른 분모로 **전부 62 · 일부 0 · 없음 202**(합 264 = 발견사항이 있는 사진 수).
 * ~~전부 55 · 일부 7~~은 **전수를 분모로 쓴 값**이고 그 7장이 바로 거짓 「일부」였다 — 이 숫자를
 * 「세 상태가 실데이터로 관측된다」의 근거로 인용하지 말 것(아래 함수 JSDoc의 1번).
 *
 * 분자와 분모가 **다른 축**(등록 성공 ↔ 등록 가능 ∪ 처리됨)이라 0이 보장은 아니고, 뭉개면
 * 운영자가 **남은 것을 못 찾는다**. `mismatch`는 아래 함수 JSDoc 참조.
 */
export type KnowledgeExportState = 'all' | 'partial' | 'mismatch';

/**
 * 「내보내기 처리」 배지 — **그릴 것이 없으면 `null`**이다(배지를 조건부로 렌더하기 위해).
 *
 * ⚠️ 라벨에 **「처리」를 반드시 남겨라** — 「내보냄」·「학습에 사용됨」으로 줄이면 **거짓**이다
 * (위 `KNOWLEDGE_EXPORT_TOOLTIP` JSDoc: 신뢰도 미달로 등록되지 않은 것도 `true`다).
 *
 * ⚠️ 빈 배지를 그리고 문구만 비우지 말 것 — 존재가 곧 상태를 뜻하는 요소라, 그렇게 하면 「안
 * 내보냈다」와 「배지가 깨졌다」가 같은 모양이 되고 그 손실이 spec에서 초록으로 지나간다
 * (`MlPhotoThumbnail`의 「삭제됨」 라벨과 같은 규율).
 *
 * ## ⚠️ 분모는 «내보내기 대상 수»다 — 전수로도, 살아 있는 전수로도 두면 거짓 「일부」가 나온다
 *
 * 분모가 두 번 좁아졌고 **두 번 다 같은 피해를 막은 것**이다(화면이 「일부」라 말하면 운영자는
 * **남은 것을 찾으러 가고 찾을 것이 없다**):
 *
 *  1. **전수 → 살아 있는 것**(2026-10-07). `findingCount`는 전수(삭제분 포함)인데
 *     `findingExportedCount`는 살아 있는 것만 센다 — 플래그를 찍는 코드
 *     (`reports/report-knowledge.service.ts` 3-3)가 `deletedAt: null`인 것만 찍으므로 삭제분은
 *     **구조적으로 분자에 못 들어간다**. 전수를 분모로 쓰면 dev에서 **7장이 거짓 「일부」**였다
 *     (전수 분모 → 전부 55·일부 7·없음 202 / 살아 있는 것 → **전부 62·일부 0·없음 202**, 합
 *     264 = 발견사항이 있는 사진 수 · 발견사항 전부 삭제 1장).
 *  2. **살아 있는 것 → 내보내기 대상**(2026-10-13, 이 함수). `10f970c16`(2026-10-12)이 플래그를
 *     「등록에 성공했다」로 좁히면서, **비AI·신뢰도 미달 발견사항은 분자에 영원히 못 들어가게**
 *     됐다 — 그런 행이 하나라도 있는 사진은 살아 있는 전수를 분모로 쓰면 **영구히 「일부」**다
 *     (dev에 비AI exported가 6건 실재한다. 즉 같은 축의 행이 실재한다).
 *
 * ⚠️ **화면에서 이 분모를 「학습 대상」이라 부르지 말 것** — 두 이유가 각각 독립이다:
 * ① `MlPhotoContextPanel`(이 배지를 그리는 **두 자리 중 하나**)의 헤더가 **「학습 대상/제외」류
 * 문구를 이 패널에 넣지 말 것**을 못박는다(정책 ⓖ: 모집단 선택은 학습 단계의 일이다).
 * ② 게이트가 넣는 곳은 **지식베이스**이고 「학습에 사용됨」이 아니다 — 아래
 * `KNOWLEDGE_EXPORT_TOOLTIP`이 그 오독을 **명시적으로 부정하는데**, 같은 배지의 분모가
 * 「학습」이라 말하면 그 문구와 모순한다. 그래서 라벨 어휘는 **「내보내기 대상」**이다.
 *
 * ⇒ 분모는 서버가 센 `findingExportTargetCount`(「등록 가능 ∪ 이미 처리됨」)다. 그 합집합 덕에
 * **분자 ⊆ 분모**가 구조적으로 참이고, 「전부」의 뜻이 **「남은 등록 가능분이 0」**으로 정직해진다.
 * 술어(AI 생성·신뢰도 임계값)와 **안 세는 셋째 조건**(원본 대책 0건)·되짚을 조건은 서버 쪽
 * `findingExportTargetCount` JSDoc이 정본이다 — 여기에 그 조건을 **다시 쓰지 말 것**.
 *
 * ## ⚠️ `mismatch`는 상태가 아니라 «덫»이다 — 오늘 도달하지 않는다
 *
 * `exported > 분모`는 **구조적으로 불가능**하다. 이유는 「두 칸을 다른 쿼리가 세는데 규약이 같다」가
 * 아니다(그 서술은 2026-10-13에 틀린 것으로 확인됐다) — **한 쿼리의 같은 배열**에서 파생된다:
 * `admin-ml.service.ts`가 `photo.findings` 한 벌을 받아 `findingDeletedCount`·
 * `findingExportedCount`·`findingExportTargetCount`를 같은 자리에서 세므로
 * `살아 있는 수 ≡ activeFindings.length`이고 분자·내보내기 대상은 **그 부분집합**이다. 항등이라
 * 입력으로 깰 수 없다.
 *
 * **오늘 이 분기의 소유자는 서버의 그 파생 자리(한 배열)다** — 이 분기가 초록인 것을 「그런 일이
 * 없다」의 근거로 인용하지 말 것. 그래도 **지우지 않는다**: 그 세 칸은 서버 투영의 규약일 뿐
 * DB 제약이 아니라, 규약이 바뀌는 날(또는 칸이 다른 쿼리로 옮겨 가는 날) 조용히 틀린 분수가
 * 나온다. 그때 **분모를 맞춰 숨기지 않고 숫자를 그대로 드러낸다**(이 화면의 `countsDisagree`
 * 검산과 같은 태도). 라벨이 **자기 분모의 이름**을 말하는 것도 그래서다.
 *
 * 「일부」도 dev 실데이터로는 관측되지 않는다(위 실측: 일부 0) — **픽스처가 유일한 검증 경로**다.
 * 그 사실을 spec 주석에도 적었다(안 적으면 다음 사람이 「실데이터로 확인됐다」로 읽는다).
 *
 * 집계를 **모를 때**(`findingCount`가 없을 때)도 `null`이다 — 「모른다」는 발견사항 칸이
 * `FINDING_COUNT_UNKNOWN_LABEL`로 한 번 말하므로, 같은 사실을 두 자리에서 말하지 않는다.
 */
export function deriveKnowledgeExportBadge(photo: {
  findingCount?: number;
  findingDeletedCount?: number;
  findingExportedCount?: number;
  findingExportTargetCount?: number;
}): { state: KnowledgeExportState; label: string } | null {
  const total = photo.findingCount;
  const exported = photo.findingExportedCount;
  if (typeof total !== 'number' || typeof exported !== 'number') return null;
  if (exported <= 0) return null;
  // 삭제 수를 모르면 **전수를 분모로 쓰지 않는다** — 그러면 위 7장이 거짓 「일부」가 된다.
  // 분수를 못 만드는 상태이므로 분수 없이 건수만 말한다.
  if (typeof photo.findingDeletedCount !== 'number') {
    return { state: 'all', label: `내보내기 처리 ${exported}건` };
  }
  const active = total - photo.findingDeletedCount;
  const target = photo.findingExportTargetCount;
  // 검산은 **아는 중에 가장 좁은 분모**로 한다(내보내기 대상 ≤ 살아 있는 수). 라벨이 자기 분모의
  // 이름을 말하므로, 어긋났을 때 **어느 규약이 깨졌는지**가 화면에서 읽힌다.
  const bound =
    typeof target === 'number'
      ? { count: target, name: '내보내기 대상' }
      : { count: active, name: '살아 있는 발견사항' };
  if (exported > bound.count) {
    return {
      state: 'mismatch',
      label: `내보내기 처리 ${exported}건 (${bound.name} ${bound.count}건)`,
    };
  }
  // 내보내기 대상 수를 **모르면** 분수를 만들지 않는다 — 살아 있는 전수로 대신하면 위 2번의 거짓
  // 「일부」가 그대로 돌아온다(서버가 이 칸을 안 내리는 상태에서 조용히 옛 동작으로 떨어지는 것이
  // 가장 나쁘다). 그래서 분수 없이 건수만 말한다.
  if (typeof target !== 'number') {
    return { state: 'all', label: `내보내기 처리 ${exported}건` };
  }
  if (exported === target) return { state: 'all', label: '전부 내보내기 처리' };
  return { state: 'partial', label: `일부 내보내기 처리 ${exported}/${target}` };
}

/**
 * 「내보내기 처리」 배지의 `title` — ⚠️ **그 칼럼이 말하는 것보다 더 말하지 않기 위한 짝이다.**
 *
 * `knowledgeExported`는 「**학습에 사용됐다**」가 아니다. ⚠️ 그런데 **그 칼럼의 뜻이 2026-10-12에
 * 바뀠고, 그래서 행 집단이 둘이다** — 뒤집어 적으면 옛 집단 쪽이 거짓이 되므로 **시점을 갈라** 적는다.
 *
 * ~~서버가 결재 완료 시 **삭제되지 않은 전 발견사항**에 플래그를 세우고, 실제 등록은
 * `aiGenerated && aiConfidence >= 0.7`인 것만 한다.~~
 * **(개정 2026-10-13 — 아래가 정본. 원 서술은 「그때 왜 그렇게 적혀 있었나」의 기록으로 남긴다.)**
 *
 * `10f970c16`(2026-10-12)이 플래그 조건을 **`!knowledgeExported` → `exported`(등록 성공)**으로
 * 좁혔다(`reports/report-knowledge.service.ts` 3-3):
 *
 *  - **그 코드가 돌기 «전»에 쓰인 행**: 「한 번 판정을 거쳤다」 — 비AI·신뢰도 미달로 **등록되지
 *    않은 것도 `true`**다(dev 실측 2026-10-07: exported 120건 중 **6건이 `ai_generated=false`**).
 *  - **그 뒤에 쓰인 행**: 「**등록에 성공했다**」 — 미달·원본 대책 0건은 `false`로 남는다.
 *
 * ⚠️ 경계는 **커밋일이 아니라 그 코드가 돈 시점**이다(배포가 뒤따른다). 행 단위로 가리려면
 * `knowledge_exported_at`을 쓰라 — 그 시각이 배포 시각보다 이르면 앞 집단이다.
 *
 * ⚠️ **그러니 「학습에 사용됨」·「학습 완료」라 쓰면 안 된다 — 두 집단 다에 대해 위험하다.**
 * 앞 집단은 등록되지 않은 것을 품고(그 거짓은 이 화면의 핵심 질문 **「무엇이 아직 학습에 안
 * 쓰였나」**을 **정반대로** 답하게 만든다 — 운영자가 등록되지도 않은 사진을 「이미 들어갔다」로
 * 넘긴다), 뒤 집단도 「지식베이스에 등록됨」까지이지 **모델이 그것으로 학습했다는 뜻이 아니다.**
 * 그래서 라벨에 **「처리」**를 박고 이 문구가 그 차이를 적는다.
 *
 * `aiEdited`와 **같은 함정**이다(위 `findingEditedCount` JSDoc) — 둘 다 **이름이 실제보다 좁은
 * 뜻을 암시하는** 칼럼이고, 둘 다 그 뜻이 schema·서비스 주석에 없다.
 */
export const KNOWLEDGE_EXPORT_TOOLTIP =
  '결재 완료 시 「내보내기 처리」를 거친 발견사항 수. 「학습에 사용됨」이 아니다. ⚠️ 2026-10-12에 이 집계의 뜻이 바뀌었다 — 그 뒤에 처리된 것은 「지식베이스 등록 성공」이고, 그 전에 처리된 것은 「한 번 판정을 거쳤다」여서 AI 생성이 아니거나 신뢰도 0.7 미달로 등록되지 않은 것도 포함한다. 분수의 분모는 「내보내기 대상」(등록 가능 + 이미 처리됨)이다.';

/** 「내보내기 처리」 배지 색 — `mismatch`는 검산 경고와 같은 빨강이어야 눈에 걸린다. */
export const KNOWLEDGE_EXPORT_COLORS: Record<KnowledgeExportState, string> = {
  all: 'bg-blue-100 text-blue-700',
  partial: 'bg-amber-100 text-amber-700',
  mismatch: 'bg-red-100 text-red-700',
};

/**
 * 발견사항 칸의 문구 — 「0건」·「모름」·「N건」을 **다른 말**로 돌려준다(위 셋 구분).
 *
 * 건수는 **전수**(삭제분 포함)다 — ML 화면의 기본이 전수이기 때문이다(가시성 계약의 admin 쪽).
 * ⚠️ 그래서 **삭제분이 있으면 그것을 적어야 한다**: dev에 발견사항이 **전부 삭제된** 사진이 1장
 * 있고(`total=2 active=0`), 적지 않으면 그 사진이 「발견사항 2건」으로만 보여 **살아 있는 것이
 * 하나도 없다는 사실이 사라진다.**
 *
 * ⚠️ **「사람 수정」이라 쓴다 — 「교정」·「정정」이라 쓰면 거짓이다.** `aiEdited`는 「AI가 틀려서
 * 사람이 바로잡았다」가 아니라 **「사람이 손댔다」**이고(위 `findingEditedCount` JSDoc에 다섯 자리와
 * 실측), 교정 쌍은 dev에 **2건**뿐인데 이 칸은 **96건**을 센다. 「사람」을 빼고 「수정 N」만 두어도
 * 안 된다 — **누가** 손댔는지가 비어 「AI가 재분석했다」로도 읽힌다.
 *
 * 「사람 수정」·「삭제」는 **0일 때 아예 붙이지 않는다** — `· 사람 수정 0`을 붙이면 그 자리가
 * 「손댄 적 없음」인지 「집계가 깨짐」인지 읽을 수 없다.
 */
export function deriveFindingLabel(photo: {
  findingCount?: number;
  findingDeletedCount?: number;
  findingEditedCount?: number;
}): string {
  const total = photo.findingCount;
  if (typeof total !== 'number') return FINDING_COUNT_UNKNOWN_LABEL;
  if (total <= 0) return NO_FINDINGS_LABEL;
  const parts = [`발견사항 ${total}건`];
  const deleted = photo.findingDeletedCount;
  if (typeof deleted === 'number' && deleted > 0) parts.push(`삭제 ${deleted}`);
  const edited = photo.findingEditedCount;
  if (typeof edited === 'number' && edited > 0) parts.push(`사람 수정 ${edited}`);
  return parts.join(' · ');
}

/**
 * 중점 분석 영역을 **그릴 수 있나** — 세 상태다.
 *
 *  - `none`    영역이 없다(대다수 — dev 273장 중 8장만 영역을 갖는다). 아무것도 말하지 않는다.
 *  - `valid`   0~1 정규화 사각형이다 → 이미지에 겹쳐 그린다.
 *  - `invalid` **값은 있는데 형상이 틀렸다** → 그리지 않고 **그 사실을 화면에 적는다.**
 *
 * ## ⚠️ `invalid`는 **이 엔드포인트로는 도달하지 않는다** — 심층 방어이고, 그래서 정직하게 적는다
 *
 * **오늘 이 축의 소유자는 서버다**: `admin-ml.service.ts`의 `toAnalysisRegion`이 같은
 * `isValidAnalysisRegion`으로 검사해 **불량이면 `null`로 내리고 `logger.warn`을 남긴다**(2026-10-07
 * 확인). 즉 이 화면이 받는 값은 **valid 아니면 null**이다. dev 8건도 전수 `valid`였다.
 *
 * ⚠️ 그러니 **이 분기가 초록인 것을 「그런 데이터가 없다」의 근거로 인용하지 말 것.** 그리고
 * **「도달 불가라 불필요」라며 지우지도 말 것** — 남긴 값은 둘이다: ① 서버의 그 정화가 사라지거나
 * 다른 데이터 경로(다른 엔드포인트·낡은 캐시)가 생기면 **그때 이 자리가 유일한 방어선**이 된다
 * (저장 자리가 jsonb라 **DB는 형상을 막지 않고**, shared 판정자의 JSDoc도 「낡은 값·사람이 psql로
 * 넣은 값」을 전제로 둔다) ② 틀린 좌표로 그린 박스는 운영자에게 **「엉뚱한 자리가 위험하다」**고
 * 말하므로, 조용한 오표시보다 시끄러운 미표시가 낫다.
 *
 * 판정은 **shared 단일 판정자에 위임한다** — 여기서 `0 ≤ x` 같은 검사를 다시 쓰면 안 된다.
 * 서버 입구 셋·분석 폴백·web 그리기 UI가 같은 함수를 쓰고, 그 JSDoc이 「자기 검사를 또 쓰면
 * 안 된다」를 명시한다. 갈리면 **서버가 받아 저장한 값을 화면이 못 그리거나** 반대가 된다.
 */
export type RegionDisplayState = 'none' | 'valid' | 'invalid';

export function deriveRegionDisplay(region: unknown): RegionDisplayState {
  if (region === null || region === undefined) return 'none';
  return isValidAnalysisRegion(region) ? 'valid' : 'invalid';
}

/**
 * 영역이 있는데 형상이 틀려 못 그릴 때의 문구 — 조용히 숨기지 않는다.
 *
 * ⚠️ **오늘 이 문구는 화면에 뜨지 않는다** — 서버가 불량을 `null`로 내리기 때문이다(위
 * `deriveRegionDisplay` JSDoc의 소유자 서술). 「쓰이지 않는 문구」로 보고 지우기 전에 그 JSDoc을
 * 읽을 것: 지우면 **그 정화가 사라진 날 틀린 박스가 조용히 그려진다.**
 */
export const REGION_INVALID_LABEL = '영역 좌표가 범위를 벗어나 표시하지 않음';

/** 영역을 그렸을 때의 설명 — 박스 자체는 시각 요소라 읽히지 않으므로 문구가 짝으로 있어야 한다. */
export const REGION_MARKED_LABEL = '이미지에 빨간 테두리로 표시';

/**
 * 등급 칸이 비어서 온 사진의 배지 문구 — **「undefined」도 빈 알약도 그리지 않는다.**
 *
 * ⚠️ **도달 창이 실재한다.** 타입(`api.ts`의 `grade: MlPhotoGrade`)은 필수라 적지만 **배포
 * 순서가 그 약속을 깬다**: `deploy-admin`(Cloudflare, ~26초)이 `deploy-ec2`(~65초)보다 **먼저**
 * 끝나므로 그 사이 새 admin이 **구 server**를 친다(그 칸은 2026-10-07에 더한 것이다). 그때
 * `photo.grade`가 `undefined`로 와서 배지가 **문구 없는 빈 알약**이 되고, 집계 쪽은 키가 문자열
 * `"undefined"`가 되어 화면에 **「undefined 7장」**이 떴다(2026-10-07 P4-h).
 *
 * ⚠️ 「모른다」를 등급 하나로 **뭉개지 말 것**(`?? 'C'` 같은 폴백 금지) — 등급은 성질 기록이고,
 * 없는 성질을 지어내면 운영자가 그 숫자를 믿는다(`FINDING_COUNT_UNKNOWN_LABEL`과 같은 규율).
 */
export const PHOTO_GRADE_UNKNOWN_LABEL = '등급 없음';

/**
 * 등급이 없는 사진을 모으는 집계 **키** — 화면 문구가 아니라 내부 버킷 이름이다.
 *
 * ⚠️ `ML_PHOTO_GRADES`에 **넣지 않는다** — 그 목록은 「서버가 주는 등급 5종」이고 이것은
 * 「등급이 안 왔다」라서 성질이 다르다(전수 분포에서 0건으로 나열할 대상도 아니다).
 * 소비처는 이 키를 만나면 `PHOTO_GRADE_UNKNOWN_LABEL`로 **옮겨 그린다**.
 */
export const PHOTO_GRADE_UNKNOWN_KEY = 'UNKNOWN';

/*
 * ⚠️ **맥락 패널은 사진 종횡비로 레이아웃을 가르지 «않는다»** — 늘 세로 배치다
 * (상단 사진 가운데 정렬 · 하단 맥락 표 전폭). **2026-10-07 사용자가 두 번 결정했고**, 두 번째는
 * 종횡비 census와 선택지 셋을 **숫자로 보고** 「항상 상단/하단」을 재확인한 것이다 — 한 화면에
 * 배치가 둘 섞이는 쪽을 기각했다.
 *
 * ⇒ **이 모듈에 종횡비 상수·측정 헬퍼를 두지 말 것.** 그 방향을 두 번 재 보고 둘 다 접었다:
 *   · **CSS `flex-wrap`** — 콘텐츠 624px에 높이 상한 46vh(≈600px)라 종횡비 0.5만 넘어도 원하는 폭이
 *     절반을 넘어, `0.57~1.00`의 **40장**이 함께 wrap되어 522×600으로 혼자 한 줄 + 옆에 102px이
 *     남는다(고치려던 결함과 같은 모양).
 *   · **JS 측정 분기** — 기제는 됐지만(타일 썸네일이 클릭 전에 로드되므로 시프트 0) **사용자가
 *     분기 자체를 기각**했다. 판정이 틀린 게 아니라 **전제가 바뀐 것**이다.
 *
 * 되짚을 조건은 **`max-h`를 낮추는 것**이고 분기 부활이 아니다. census·근거는
 * `components/MlPhotoContextPanel.tsx` 레이아웃 주석이 정본이다.
 */
